import React, { useRef, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import api from '~/services/api';
import { toast } from 'react-toastify';
import { isArray } from 'lodash';
import { LocalShipping, Edit, Visibility } from '@material-ui/icons';
import { UnidataTable } from '~/components';
import { number } from '~/helpers';

import { format, setHours, setMinutes, setSeconds } from 'date-fns';
import {
  deleteOperacaoPreRecebimento,
  recordFiltering,
} from '~/store/modules/operacaoprerecebimento/actions';

import {
  ADM,
  GFO,
  GPP,
  FRENTISTA,
  CONSULTA_OPERACAO,
} from '~/constants/perfil';

import OperacaoPreRecebimentoForm from './form';

import GPPConfirmar from './components/GPPConfirmar';

import Detalhe from './components/Detalhe';

import EditarPreRecebimento from './components/EditarPreRecebimento';

function OperacaoPreRecebimento() {
  const editarPreRecebimentoRef = useRef(null);
  const gppConfirmarRef = useRef(null);
  const detalheRef = useRef(null);
  const dispatch = useDispatch();
  const [modal, setModal] = React.useState(false);
  const [habConfirmar, setHabConfirmar] = React.useState(false);
  const [habAlterar, setHabAlterar] = React.useState(false);
  const [habCreate, setHabCreate] = React.useState(false);
  const [habDetalhe, setHabDetalhe] = React.useState(false);
  const [params, setParams] = React.useState({});
  const [itemSelecionado, setItemSelecionado] = React.useState({});
  const buttons = [];
  const { profile } = useSelector((state) => state.user);

  React.useEffect(() => {
    if (modal) {
      editarPreRecebimentoRef.current.openModal();
    }
  }, [modal]);

  const operacaoprerecebimento = useSelector(
    (state) => state.operacaoprerecebimento
  );

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      switch (profile.gloperfilid) {
        case ADM:
        case GFO:
          setHabConfirmar(false);
          setHabAlterar(false);
          setHabCreate(false);
          setHabDetalhe(true);
          break;
        case GPP:
        case FRENTISTA:
          setHabConfirmar(true);
          setHabAlterar(true);
          setHabCreate(true);
          setHabDetalhe(true);
          break;
        case CONSULTA_OPERACAO:
          setHabConfirmar(true);
          setHabAlterar(false);
          setHabCreate(false);
          setHabDetalhe(true);
          break;
        default:
          setHabConfirmar(false);
          setHabAlterar(false);
          setHabDetalhe(true);
      }
    }
    return function cleanup() {
      mounted = false;
    };
  }, []);

  const handleUpdate = React.useCallback(async () => {
    await dispatch(recordFiltering(operacaoprerecebimento.params));
  }, [operacaoprerecebimento]);

  // Close Modal Edit
  const handleCloseModalEdit = React.useCallback(() => {
    setModal(false);
    setParams({});
    dispatch(recordFiltering(operacaoprerecebimento.params));
  }, [operacaoprerecebimento]);

  const handleOpenModalEdit = React.useCallback(
    async (values) => {
      if (values.prre_status > 0) {
        toast.error('Não é permitido essa operação nesse registro!');
        return;
      }
      try {
        const { data } = await api.get(`prerecebimento/${values.prre_codigo}`);
        setModal(true);
        setParams(data);
      } catch (err) {
        if (isArray(err)) {
          err.map((e) => toast.error(e));
        }
      }
    },
    [modal]
  );

  const handleConfirmarReg = React.useCallback(
    (values) => {
      gppConfirmarRef.current.handleToggleModal(values);
    },
    [gppConfirmarRef]
  );

  const handleDetalheReg = React.useCallback(
    (values) => {
      detalheRef.current.handleToggleModal(values);
    },
    [detalheRef]
  );

  const verificarPendenciaConfirmar = React.useCallback((values) => {
    if (profile.gloperfilid === 6) {
      if (values.desc_pendente !== 'S') {
        toast.error(
          'Não é permitido essa operação no registro de recebimento!'
        );
        return true;
      }
    } else if (profile.gloperfilid === 3) {
      if (values.desc_pendente_orgao !== 'S') {
        toast.error(
          'Não é permitido essa operação no registro de recebimento!'
        );
        return true;
      }
    }
  }, []);

  const datahorainicial = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(new Date(), 0), 0), 0),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const datahorafinal = useMemo(
    () =>
      format(
        setSeconds(setMinutes(setHours(new Date(), 23), 59), 59),
        'dd/MM/yyyy HH:mm:ss'
      ),
    []
  );

  const filter = useMemo(
    () => [
      {
        id: 'data_cadastro_ini',
        label: 'Data Inicial',
        type: 'initialdatetime',
        defaultValue: datahorainicial,
      },
      {
        id: 'data_cadastro_fim',
        label: 'Data final',
        type: 'finaldatetime',
        defaultValue: datahorafinal,
      },
    ],
    [datahorainicial, datahorafinal]
  );

  const header = React.useMemo(
    () => [
      {
        id: 'prre_codigo',
        label: 'Recebimento Provisório',
        filter: {
          type: 'text',
        },
        align: 'left',
      },
      {
        id: 'poco_descricao',
        label: 'Posto',
        filter: {
          type: 'text',
        },
        align: 'left',
      },
      {
        id: 'prre_data_cadastro',
        label: 'Data Cadastro',
        formatted: (value) => moment(value).format('DD/MM/YYYY HH:mm'),
      },
      {
        id: 'prod_display',
        label: 'Produto',
        filter: {
          type: 'text',
        },
        align: 'left',
      },
      {
        id: 'prre_volume_total',
        label: 'Volume Total',
        align: 'right',
        formatted: (value) => number(value, 2),
      },
      {
        id: 'prre_status_str',
        label: 'Status',
        filter: {
          type: 'text',
        },
        align: 'left',
      },
      {
        id: 'prre_cosi_contratogta',
        label: 'Contrato nº',
        filter: {
          type: 'text',
        },
        align: 'left',
      },
      {
        id: 'prre_num_ano_empenho',
        label: 'Nº/Ano do Empenho',
        filter: {
          type: 'text',
        },
        align: 'left',
      },
      {
        id: 'usua_login_recebedor_responsavel_str',
        label: 'Recebedor Responsável',
        filter: {
          type: 'text',
        },
        align: 'left',
      },
    ],
    []
  );

  const tituloButtonConfirmar = useMemo(() => {
    let titulo = '';
    switch (profile.gloperfilid) {
      case GPP:
      case GFO:
      case FRENTISTA:
        titulo = 'Confirmar Recebimento';
        break;
      case ADM:
      case CONSULTA_OPERACAO:
        titulo = 'Detalhe Recebimento';
        break;
      default:
        titulo = 'Detalhe Recebimento';
    }
    return titulo;
  }, []);

  const headerbuttons = useMemo(
    () => [
      habConfirmar &&
      itemSelecionado.prre_status === 2 &&
      buttons.push({
        title: tituloButtonConfirmar,
        action: (value) => handleConfirmarReg(value),
        icon: <LocalShipping />,
      }),
      habDetalhe &&
      itemSelecionado.prre_status !== 2 &&
      buttons.push({
        title: 'Detalhe Recebimento',
        action: (value) => handleDetalheReg(value),
        icon: <Visibility />,
      }),
      habAlterar &&
      itemSelecionado.prre_status === 0 &&
      buttons.push({
        title: 'Editar Recebimento',
        action: (value) => handleOpenModalEdit(value),
        icon: <Edit />,
      }),
    ],
    [
      operacaoprerecebimento,
      itemSelecionado,
      habConfirmar,
      habDetalhe,
      habAlterar,
    ]
  );

  const registroSelecionado = React.useCallback((values) => {
    setItemSelecionado(values);
  }, []);

  return (
    <>
      <UnidataTable
        pdf
        excel
        reportOrder="prre_codigo ASC"
        title={'Cadastro de Recebimento Provisório'}
        create={profile.gloperfilid === GPP}
        delete={!(itemSelecionado.prre_status === 4)}
        update={false}
        fixed
        header={header}
        form={OperacaoPreRecebimentoForm}
        custom={filter}
        redux={operacaoprerecebimento}
        remove={deleteOperacaoPreRecebimento}
        read={recordFiltering}
        actions={buttons}
        detach="prre_codigo"
        keys="prre_codigo"
        maxWidth="md"
        onRow={(values) => registroSelecionado(values)}
        view="VW_PRE_RECEBIMENTO"
        reportTitle="Cadastro de Recebimento Provisório"
      />

      <GPPConfirmar
        ref={gppConfirmarRef}
        update={() => handleUpdate()}
        name="GPPConfirmar"
      />

      <Detalhe ref={detalheRef} update={() => handleUpdate()} name="Detalhe" />

      {modal && (
        <EditarPreRecebimento
          ref={editarPreRecebimentoRef}
          onCloseModal={handleCloseModalEdit}
          formEdit={params}
        />
      )}
    </>
  );
}

export default OperacaoPreRecebimento;
